import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PositionCombobox, SeniorityCombobox } from '@/components/shared';
import { ROLE_ADMIN, useAuth } from '@/contexts/AuthContext';
import { usePositions } from '@/hooks/usePositions';
import { useSeniorities } from '@/hooks/useSeniorities';
import {
    EVALUATION_AXIS_KEYS,
    EVALUATION_AXIS_LABELS,
    deleteEvaluationThresholdConfig,
    listEvaluationThresholdConfigs,
    saveEvaluationThresholdConfig,
    type EvaluationAxisKey,
    type EvaluationThresholdConfig,
} from '@/features/People/Evaluations/services/evaluationThresholds';

type ThresholdFormValues = Record<EvaluationAxisKey, string>;

interface FormState {
    positionId: string;
    seniorityId: string;
    thresholds: ThresholdFormValues;
}

const emptyThresholds = (): ThresholdFormValues => EVALUATION_AXIS_KEYS.reduce<ThresholdFormValues>((acc, axis) => {
    acc[axis] = '';
    return acc;
}, {} as ThresholdFormValues);

const emptyForm = (): FormState => ({
    positionId: '',
    seniorityId: '',
    thresholds: emptyThresholds(),
});

function formatPercent(value?: number | null): string {
    return typeof value === 'number' && Number.isFinite(value)
        ? value.toLocaleString('pt-BR', { style: 'percent', maximumFractionDigits: 0 })
        : '-';
}

function parseThreshold(value: string): number | null {
    const normalized = value.replace(',', '.');
    const numeric = Number.parseFloat(normalized);

    if (!Number.isFinite(numeric) || numeric < 0 || numeric > 100) {
        return null;
    }

    return numeric / 100;
}

function configToForm(config: EvaluationThresholdConfig): FormState {
    return {
        positionId: config.positionId,
        seniorityId: config.seniorityId,
        thresholds: EVALUATION_AXIS_KEYS.reduce<ThresholdFormValues>((acc, axis) => {
            const value = config.thresholds[axis];
            acc[axis] = typeof value === 'number' ? String(Math.round(value * 100)) : '';
            return acc;
        }, {} as ThresholdFormValues),
    };
}

export default function EvaluationThresholdsPage() {
    const { user, userHasRole } = useAuth();
    const { positions, loading: positionsLoading, error: positionsError } = usePositions();
    const { seniorities, loading: senioritiesLoading, error: senioritiesError } = useSeniorities();
    const [configs, setConfigs] = useState<EvaluationThresholdConfig[]>([]);
    const [form, setForm] = useState<FormState>(() => emptyForm());
    const [editingConfig, setEditingConfig] = useState<EvaluationThresholdConfig | null>(null);
    const [loadingConfigs, setLoadingConfigs] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const canViewAdminPage = useMemo(() => {
        if (!user) return false;
        return userHasRole(ROLE_ADMIN);
    }, [user, userHasRole]);

    const loadConfigs = useCallback(async () => {
        setLoadingConfigs(true);
        try {
            const data = await listEvaluationThresholdConfigs();
            setConfigs(data);
        } catch (err) {
            console.error('Erro ao buscar parâmetros de avaliação:', err);
            setError('Erro ao buscar parâmetros de avaliação.');
        } finally {
            setLoadingConfigs(false);
        }
    }, []);

    useEffect(() => {
        if (canViewAdminPage) {
            void loadConfigs();
        } else {
            setLoadingConfigs(false);
        }
    }, [canViewAdminPage, loadConfigs]);

    const selectedPosition = positions.find((position) => position.id === form.positionId) ?? null;
    const selectedSeniority = seniorities.find((seniority) => seniority.id === form.seniorityId) ?? null;
    const isLoading = loadingConfigs || positionsLoading || senioritiesLoading;

    const handleThresholdChange = (axis: EvaluationAxisKey, value: string) => {
        setForm((prev) => ({
            ...prev,
            thresholds: {
                ...prev.thresholds,
                [axis]: value,
            },
        }));
    };

    const resetForm = () => {
        setForm(emptyForm());
        setEditingConfig(null);
        setError(null);
        setSuccess(null);
    };

    const handleEdit = (config: EvaluationThresholdConfig) => {
        setEditingConfig(config);
        setForm(configToForm(config));
        setError(null);
        setSuccess(null);
    };

    const handleDelete = async (config: EvaluationThresholdConfig) => {
        if (!window.confirm(`Excluir parâmetros para ${config.positionName} / ${config.seniorityName}?`)) {
            return;
        }

        try {
            await deleteEvaluationThresholdConfig(config.id);
            setSuccess('Parâmetro excluído com sucesso.');
            if (editingConfig?.id === config.id) {
                setEditingConfig(null);
                setForm(emptyForm());
            }
            await loadConfigs();
        } catch (err) {
            console.error('Erro ao excluir parâmetros de avaliação:', err);
            setError('Erro ao excluir parâmetros de avaliação.');
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsSubmitting(true);
        setError(null);
        setSuccess(null);

        if (!selectedPosition || !selectedSeniority) {
            setError('Selecione cargo e senioridade.');
            setIsSubmitting(false);
            return;
        }

        const thresholds = EVALUATION_AXIS_KEYS.reduce<Record<string, number>>((acc, axis) => {
            const value = parseThreshold(form.thresholds[axis]);
            if (value !== null) {
                acc[axis] = value;
            }
            return acc;
        }, {});

        if (Object.keys(thresholds).length !== EVALUATION_AXIS_KEYS.length) {
            setError('Informe percentuais válidos entre 0 e 100 para todos os eixos.');
            setIsSubmitting(false);
            return;
        }

        try {
            await saveEvaluationThresholdConfig({
                positionId: selectedPosition.id,
                positionName: selectedPosition.name,
                seniorityId: selectedSeniority.id,
                seniorityName: selectedSeniority.name,
                thresholds,
            });
            setSuccess(editingConfig ? 'Parâmetro atualizado com sucesso.' : 'Parâmetro criado com sucesso.');
            setEditingConfig(null);
            setForm(emptyForm());
            await loadConfigs();
        } catch (err) {
            console.error('Erro ao salvar parâmetros de avaliação:', err);
            setError('Erro ao salvar parâmetros de avaliação.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!user) {
        return <div className="text-gray-500">Carregando usuário...</div>;
    }

    if (!canViewAdminPage) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="text-center">
                    <h1 className="mb-4 text-2xl font-semibold text-gray-700">Acesso Negado</h1>
                    <p className="text-gray-500">Você não tem permissão para visualizar esta página.</p>
                    <Link to="/" className="mt-6 inline-block rounded bg-indigo-600 px-6 py-2 text-white hover:bg-indigo-700">
                        Voltar para o Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return <div className="text-gray-500">Carregando parâmetros de avaliação...</div>;
    }

    return (
        <div className="space-y-8">
            <div>
                <h2 className="text-2xl font-bold text-gray-900">Parâmetros de Avaliação</h2>
                <p className="mt-1 text-sm text-gray-500">
                    Configure o percentual esperado pela Raro por cargo, senioridade e eixo.
                </p>
            </div>

            <div className="bg-white shadow sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                        {editingConfig ? 'Editar Parâmetro' : 'Cadastrar Parâmetro'}
                    </h3>
                    {error && (
                        <div className="mt-3 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-600">
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className="mt-3 rounded border border-green-200 bg-green-50 p-2 text-sm text-green-700">
                            {success}
                        </div>
                    )}
                    <form onSubmit={handleSubmit} className="mt-5 space-y-5">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Cargo
                                </label>
                                <PositionCombobox
                                    selectedId={form.positionId}
                                    onChange={(position) => setForm((prev) => ({ ...prev, positionId: position?.id || '' }))}
                                    disabled={isSubmitting}
                                    positions={positions}
                                    loading={positionsLoading}
                                    error={positionsError}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Senioridade
                                </label>
                                <SeniorityCombobox
                                    selectedId={form.seniorityId}
                                    onChange={(seniority) => setForm((prev) => ({ ...prev, seniorityId: seniority?.id || '' }))}
                                    disabled={isSubmitting}
                                    seniorities={seniorities}
                                    loading={senioritiesLoading}
                                    error={senioritiesError}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                            {EVALUATION_AXIS_KEYS.map((axis) => (
                                <div key={axis}>
                                    <label htmlFor={`threshold-${axis}`} className="block text-sm font-medium text-gray-700">
                                        {EVALUATION_AXIS_LABELS[axis]} (%)
                                    </label>
                                    <input
                                        id={`threshold-${axis}`}
                                        type="number"
                                        min={0}
                                        max={100}
                                        step={1}
                                        className="input-primary mt-1 w-full"
                                        value={form.thresholds[axis]}
                                        onChange={(event) => handleThresholdChange(axis, event.target.value)}
                                        required
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-end gap-3">
                            {editingConfig && (
                                <button type="button" onClick={resetForm} className="btn-secondary">
                                    Cancelar
                                </button>
                            )}
                            <button type="submit" className="btn-primary" disabled={isSubmitting}>
                                {isSubmitting ? 'Salvando...' : editingConfig ? 'Atualizar' : 'Salvar'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <div className="bg-white shadow sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                    <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900">Parâmetros Cadastrados</h3>
                    {configs.length === 0 ? (
                        <p className="text-sm text-gray-500">Nenhum parâmetro cadastrado.</p>
                    ) : (
                        <div className="w-full overflow-x-auto">
                            <table className="w-full min-w-[860px] divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Cargo</th>
                                        <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Senioridade</th>
                                        {EVALUATION_AXIS_KEYS.map((axis) => (
                                            <th key={axis} className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                                                {EVALUATION_AXIS_LABELS[axis]}
                                            </th>
                                        ))}
                                        <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {configs.map((config) => (
                                        <tr key={config.id}>
                                            <td className="px-5 py-4 text-sm font-medium text-gray-900">{config.positionName}</td>
                                            <td className="px-5 py-4 text-sm text-gray-700">{config.seniorityName}</td>
                                            {EVALUATION_AXIS_KEYS.map((axis) => (
                                                <td key={axis} className="px-4 py-4 text-center text-sm text-gray-700">
                                                    {formatPercent(config.thresholds[axis])}
                                                </td>
                                            ))}
                                            <td className="whitespace-nowrap px-5 py-4 text-right text-sm font-medium">
                                                <button
                                                    type="button"
                                                    onClick={() => handleEdit(config)}
                                                    className="mr-3 text-blue-600 hover:text-blue-900"
                                                    title="Editar"
                                                >
                                                    <PencilIcon className="h-5 w-5" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => void handleDelete(config)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Excluir"
                                                >
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
